import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { clientLog } from "../lib/clientLog";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
//
// v1.16 — visual refresh (monogram layout). The Emergent Auth redirect below
// is byte-identical to the previous version; only the surrounding chrome changed.
//
// Layout language: editorial / branded-letterhead. A huge low-opacity olive VOW
// monogram bleeds off both sides as background art; the sign-in card sits inside
// it. Hairline corner annotations frame the page. Motion is restrained —
// staggered card fade-in + refined button hover — and respects prefers-reduced-motion.

export default function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    let msg = null;
    if (location.state?.error) msg = location.state.error;
    try {
      const stored = sessionStorage.getItem("vowos_login_error");
      if (stored) { msg = stored; sessionStorage.removeItem("vowos_login_error"); }
    } catch (e) { /* noop */ }
    if (msg) setAuthError(msg);
  }, [location.state]);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [loading, user, navigate]);

  const handleLogin = () => {
    setAuthError(null);
    const redirectUrl = window.location.origin + "/dashboard";
    clientLog("login_click", "User tapped Sign in with Google", {
      ua_mobile: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent || ""),
      redirect: redirectUrl,
    });
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-white text-slate-900 antialiased login-root">
      {/* Giant olive monogram backdrop — purely decorative */}
      <div className="absolute inset-0 grid place-items-center pointer-events-none" aria-hidden="true">
        <div className="login-monogram select-none">VOW</div>
      </div>

      {/* Editorial corner annotations */}
      <div className="absolute top-6 left-6 text-[10px] uppercase tracking-[0.3em] text-slate-400">
        Voyage of Wellness
      </div>
      <div className="absolute top-6 right-6 text-[10px] uppercase tracking-[0.3em] text-slate-400">
        Internal
      </div>
      <div className="absolute bottom-5 left-6 text-[10px] text-slate-400">
        © {new Date().getFullYear()}
      </div>
      <div className="absolute bottom-5 right-6 text-[10px] uppercase tracking-[0.3em] text-olive">
        Control
      </div>

      <main className="relative min-h-screen flex items-center justify-center px-5 lg:px-10">
        <div className="w-full max-w-md login-anim-row" style={{ animationDelay: "0ms" }}>
          <div
            data-testid="login-card"
            className="bg-white border border-slate-200 rounded-2xl p-7 lg:p-9 shadow-[0_8px_32px_rgba(15,23,42,0.06)]"
          >
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-olive">Welcome</div>
              <h1 className="mt-1 text-3xl lg:text-[32px] font-semibold tracking-tight leading-tight text-slate-900">
                VOW Control
              </h1>
              <p className="mt-2.5 text-sm lg:text-[15px] text-slate-600 leading-relaxed">
                Sign in with your team Google account to continue.
              </p>
            </div>

            {authError && (
              <div
                data-testid="login-error-banner"
                role="alert"
                className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3"
              >
                <div className="text-[11px] font-semibold uppercase tracking-wider text-red-700">Sign-in failed</div>
                <div className="text-sm text-red-900 mt-1 leading-relaxed">{authError}</div>
                <button
                  data-testid="dismiss-login-error"
                  onClick={() => setAuthError(null)}
                  className="mt-2 text-xs font-semibold text-red-700 hover:text-red-900 underline underline-offset-2"
                >
                  Dismiss
                </button>
              </div>
            )}

            <button
              data-testid="google-signin-btn"
              onClick={handleLogin}
              className="login-cta mt-6 w-full rounded-xl px-5 py-3.5 inline-flex items-center justify-center gap-3 bg-slate-900 text-white text-sm font-semibold transition-all"
            >
              <GoogleIcon /> Continue with Google
            </button>
            <p className="mt-3 text-[11px] text-slate-500 text-center">
              Access is restricted to authorized team accounts.
            </p>
          </div>
        </div>
      </main>

      <style>{`
        .login-anim-row {
          opacity: 0;
          transform: translateY(6px);
          animation: login-row-in 520ms cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
        }
        @keyframes login-row-in { to { opacity: 1; transform: translateY(0); } }

        .login-cta:hover {
          background-color: #6B7F3F;
          box-shadow: 0 8px 20px rgba(107, 127, 63, 0.24);
        }
        .login-cta:active { transform: scale(0.985); }

        .login-monogram {
          font-family: Inter, ui-sans-serif, system-ui;
          font-weight: 900;
          font-size: clamp(280px, 50vw, 720px);
          letter-spacing: -0.06em;
          line-height: 1;
          color: #6B7F3F;
          opacity: 0.06;
        }

        @media (prefers-reduced-motion: reduce) {
          .login-anim-row { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </div>
  );
}

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#ffffff"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#ffffff" opacity=".9"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#ffffff" opacity=".75"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#ffffff" opacity=".6"/>
  </svg>
);
